import { useCallback, useEffect, useRef, useState } from 'react'
import { useUserRole } from '@/hooks/useUserRole'
import { showApiError, showInfo } from '@/lib/utils/notifications'
import {
  getLatestReportExportStatus,
  triggerReportExport,
} from '../services/reportsService'
import { POLL_INTERVAL_MS } from '../constants/exportPolling'

export function useReportExportPolling(reportType: string) {
  const { user } = useUserRole()
  const userId = (user as { user_id?: number | string } | undefined)?.user_id
  const [status, setStatus] = useState<{
    status?: string
    file_path?: string
    created_at?: string
  } | null>(null)
  const [triggering, setTriggering] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const checkStatus = useCallback(async () => {
    if (userId == null) return
    try {
      const next = await getLatestReportExportStatus(userId, reportType)
      setStatus(next)
      if (next.status?.toUpperCase() !== 'PROCESSING') stopPolling()
    } catch (e) {
      showApiError(e)
      stopPolling()
    }
  }, [userId, reportType, stopPolling])

  const startPolling = useCallback(() => {
    stopPolling()
    void checkStatus()
    intervalRef.current = setInterval(() => {
      void checkStatus()
    }, POLL_INTERVAL_MS)
  }, [checkStatus, stopPolling])

  const triggerExport = useCallback(
    async (exportPath: string, searchParams: URLSearchParams) => {
      setTriggering(true)
      try {
        const result = await triggerReportExport(exportPath, searchParams)
        const msg =
          typeof result?.message === 'string' && result.message.trim()
            ? result.message.trim()
            : 'Export queued. Download will be available when processing completes.'
        showInfo(msg)
        startPolling()
      } catch (e) {
        showApiError(e)
      } finally {
        setTriggering(false)
      }
    },
    [startPolling],
  )

  const dismissStatus = useCallback(() => {
    stopPolling()
    setStatus(null)
  }, [stopPolling])

  useEffect(() => {
    void checkStatus()
    return stopPolling
  }, [checkStatus, stopPolling])

  return {
    status,
    triggering,
    isProcessing: status?.status?.toUpperCase() === 'PROCESSING',
    triggerExport,
    refreshStatus: checkStatus,
    dismissStatus,
  }
}
